import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  captureDocumentViewerEscapePrecedence,
  shouldHandleDocumentViewerEscape,
} from "../FileViewer/documentViewerEscape";
import { useDispatch } from "react-redux";
import { Drawer } from "@mui/material";
import { Resizable } from "re-resizable";

import { setFileViewerDataOnlyNew } from "@/redux/fileViewerSlice";
import FileViewer from "../FileViewer";
import IodlSourceViewer from "../FileViewer/IodlSourceViewer";
import DiagramPanel from "../FileViewer/DiagramPanel";
import TextArtifactPanel from "../FileViewer/TextArtifactPanel";
import TableArtifactPanel from "../FileViewer/TableArtifactPanel";
import type { TableCellLink } from "../FileViewer/tableCellLink";
import ChatEditorPanel from "../FpPages/Chat/components/ChatEditorPanel";
import RmsEntityPreview from "../FpPages/RmsEntityPreview";
import {
  CLOSE_ARTIFACT_OVERLAY_EVENT,
  OPEN_ARTIFACT_OVERLAY_EVENT,
  getArtifactViewerMode,
  getViewerMode,
  VIEWER_MODE_CHANGE_EVENT,
} from "../FpPages/viewerMode";
import { shouldNavigateToEntity } from "../FpPages/RMS/rmsEntityRouting";
import type { EntityKind } from "../FpPages/RMS/Dashboard/types";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import useDismissOnBack from "@/hooks/useDismissOnBack";
import { motionTokenMs } from "@/utils/motionTokenMs";
import { clearProductTimeout, setProductTimeout } from "@/utils/productTimers";
import { opensInDrawer, useDrawerArtifactStore } from "./drawerArtifactStore";
import { documentCloseDetail } from "../FileViewer/documentClose";
import PaneDivider from "./PaneDivider";

const MIN_WIDTH_VW = 30;
const MAX_WIDTH_VW = 95;
/** How long a phone close keeps re-stripping ?docId from the pops it causes. */
const POP_STRIP_WINDOW_MS = 1000;

/** The widest the drawer may be, in pixels at the current window width. */
function getMaxWidth() {
  return Math.round(window.innerWidth * (MAX_WIDTH_VW / 100));
}

/** Every fresh open starts at the widest: overlay exists to see more of the
 *  document, and at half the window it is the split pane under a scrim
 *  (#14664). It is the clamp's own maximum, so a resize never snaps a fresh
 *  open. A drag lasts until the drawer closes and is never remembered across
 *  opens (Gerry, 2026-10-13: a remembered drag made it open at a different
 *  width each time, #13345). */
function getDefaultWidth() {
  return getMaxWidth();
}

function clampWidth(width: number) {
  const min = Math.round(window.innerWidth * (MIN_WIDTH_VW / 100));
  return Math.max(min, Math.min(getMaxWidth(), width));
}

/** The drawer's left edge: re-resizable owns the drag (this is its handle
 * component, inside its 14px handle box), so the app's one divider draws
 * the line only (`passive`). */
const LeftHandle = ({ width }: { width: number }) => (
  <div
    style={{
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      // A generous grab zone on the drawer's own edge (Gerry: the bar "is
      // hard to get to. It only shows when you are in a really tight
      // range").
      width: 14,
      cursor: "col-resize",
      zIndex: 10,
    }}
    className="group/divider"
  >
    <PaneDivider
      label="Resize document viewer"
      placement="left"
      value={width}
      min={0}
      max={width}
      passive
      // The line on the drawer's own edge, inside its handle box.
      offset={5}
    />
  </div>
);

/** The openDocumentPanel detail shapes the drawer can host besides a plain
 *  file — the artifact editor panes (Gerry, 2026-09-01: overlay works for
 *  every artifact kind). Kept verbatim so a mode flip re-dispatches the
 *  exact detail back to the split pane. */
interface ArtifactPaneDetail {
  fileName?: string;
  diagram?: string;
  artifactId?: string | null;
  artifactEditable?: boolean;
  textArtifact?: {
    artifactId: string;
    editable?: boolean;
    title: string;
    filename: string;
    downloadUrl: string;
  };
  tableArtifact?: {
    artifactId: string;
    editable?: boolean;
    title: string;
    content: string;
    // What the citation that opened it cited (#12818): the pane marks it.
    citedValues?: readonly string[];
    // A deep link to one cell's source (#13788): the pane opens it.
    cellLink?: TableCellLink;
  };
  editorDocument?: {
    workspaceId: string;
    title: string | null;
    artifactId: string | null;
  };
  previewUrl?: string;
  downloadUrl?: string;
}

interface EntityPreviewEntry {
  entityKind: EntityKind;
  entityId: string;
  // The attributes a citation named, highlighted on open (#11369). Same shape
  // as Layout's entry so the two panes hold a preview identically, and stable
  // across renders — the preview re-seeds its selection whenever this prop's
  // identity changes, so building the array in JSX would discard a highlight
  // the reader added every time the drawer re-rendered.
  factNames?: string[];
}

const DocumentViewDrawer = () => {
  const [open, setOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(getDefaultWidth);
  const [entityPreviewStack, setEntityPreviewStack] = useState<
    EntityPreviewEntry[]
  >([]);
  const [artifactPane, setArtifactPane] = useState<ArtifactPaneDetail | null>(
    null
  );
  const dispatch = useDispatch();
  const fileViewerData = useSelector(
    (state: RootState) => state.fileViewerData
  );
  // At phone the drawer presents FULL SCREEN with no resize affordance —
  // removing the Resizable wrapper removes its drag listeners too, not
  // just its pixels (#11742 round 5 T8, CD). The stored desktop width is
  // neither read into the layout nor written from a phone session.
  const phone = useIsPhoneViewport();

  // Clean up docId from the URL when the viewer closes. Carries
  // history.state forward (#11742): a bare `{}` wipes the phone overlay's
  // back-dismissal marker and react-router's idx bookkeeping.
  const stripDocId = ({ andInteraction = false } = {}) => {
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has("docId")) {
      currentUrl.searchParams.delete("docId");
      // A note opened over an artifact carries its interaction too; the
      // document's own close strips both, and so does back (#12452).
      if (andInteraction) currentUrl.searchParams.delete("interactionId");
      window.history.replaceState(
        window.history.state ?? {},
        "",
        currentUrl.toString()
      );
    }
  };

  // What the drawer shows stays on screen for the whole slide out (#12481,
  // Gerry: "some scrollbars show up and the transition looks a bit wonky"):
  // the close clears the artifact, the record stack and the document only
  // once the exit has ended. Each clear is conditional on the state still
  // being what it was at the close, so a reopen that lands before the exit
  // ends (which cancels it, and with it `onExited`) is never wiped: the
  // queue is flushed on that reopen too, and its clears find new state.
  const exitQueueRef = useRef<(() => void)[]>([]);
  // What a hand-off has let go of but still shows while it slides out: the
  // open effect below must not count it, or the document the hand-off
  // re-places would reopen the drawer on top of the departing artifact.
  const departingRef = useRef<{
    pane: ArtifactPaneDetail | null;
    stack: EntityPreviewEntry[] | null;
  }>({ pane: null, stack: null });
  const flushExitQueue = () => {
    const queue = exitQueueRef.current;
    exitQueueRef.current = [];
    for (const clear of queue) clear();
  };
  // A hand-off to the split pane slides out while the pane slides in
  // (Gerry: "switching from overlay to side by side is not smooth at
  // all"). A live document too (Gerry: "it is still happening, only for
  // docx artifacts"): its departing copy tells its editor not to hand the
  // shared slice back on unmount, since the split pane's copy owns it now.
  // The current close handler, for the document-level Escape listener
  // registered once below.
  const handleCloseRef = useRef<() => void>(() => {});

  // The state as of the last render, read through the ref rather than the
  // closure (review B1): a listener that held the first render's
  // `handleClose` saw an empty drawer in its closure, and the clears it
  // queued compared against that and kept the artifact for the next
  // document to open beside.
  const handleClose = () => {
    const paneAtClose = handoffRef.current.artifactPane;
    const stackAtClose = handoffRef.current.entityPreviewStack;
    const dataAtClose = handoffRef.current.fileViewerData;
    setOpen(false);
    stripDocId();
    exitQueueRef.current.push(() => {
      setArtifactPane((prev) => (prev === paneAtClose ? null : prev));
      setEntityPreviewStack((prev) => (prev === stackAtClose ? [] : prev));
      if (handoffRef.current.fileViewerData === dataAtClose) {
        dispatch(setFileViewerDataOnlyNew({ isOpen: false }));
      }
    });
  };
  handleCloseRef.current = handleClose;

  // At phone the viewer's back-dismissal entries are still on the stack
  // when it closes: the close handler's strip edits an entry the dismissal
  // pops are about to consume, and the pop LANDS on the pre-viewer entry —
  // which, on a deep link, still carries ?docId (a reload there would
  // reopen the document the user just closed). Watch the close transition
  // itself, because the viewer's own ✕ routes through FileViewer's close
  // handler rather than ours, and re-strip on each pop until the dismissal
  // settles.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!phone || !wasOpen || open) return;
    const onPop = () => stripDocId();
    window.addEventListener("popstate", onPop);
    // The listening window's end. The dismissal's pops carry no "done"
    // signal, so this is a deadline, not a wait for state; it is owned by the
    // effect, which clears it, where a delay's promise outlived an unmount
    // (#12187).
    const deadline = setProductTimeout(
      () => window.removeEventListener("popstate", onPop),
      POP_STRIP_WINDOW_MS
    );
    return () => {
      clearProductTimeout(deadline);
      window.removeEventListener("popstate", onPop);
    };
  }, [open, phone]);

  // The phone viewer is a pushed state like every other phone overlay
  // (batch review): hardware back dismisses IT, not the page beneath —
  // Details inside arms its own level on top via the same hook. Back steps
  // DOWN, not out (CD: "back means up the hierarchy") — a stacked entity
  // preview pops one level per press, same as the in-pane Back arrow and
  // Layout's split-path twin; the stack length as renewKey re-arms a fresh
  // entry for each level of the descent.
  // A document opened from an artifact the drawer is showing (#12452): the
  // two are read together, so the artifact stays. Side by side where the
  // drawer has the width; at phone the document is a level above the
  // artifact, and back returns to it.
  const documentOpen = fileViewerData.isOpen && !fileViewerData.doNotOpenDrawer;
  const documentOverArtifact = documentOpen && artifactPane !== null;
  const dismissTopDrawerLevel = () => {
    // A record preview steps down to the one beneath it, or to the artifact
    // it was opened from, as its own back arrow does.
    if (
      entityPreviewStack.length > 1 ||
      (entityPreviewStack.length === 1 && artifactPane !== null)
    ) {
      setEntityPreviewStack((prev) => prev.slice(0, -1));
      return;
    }
    if (entityPreviewStack.length === 0 && documentOverArtifact) {
      dispatch(setFileViewerDataOnlyNew({ isOpen: false }));
      stripDocId({ andInteraction: true });
      return;
    }
    handleClose();
  };
  useDismissOnBack(
    phone && open,
    dismissTopDrawerLevel,
    entityPreviewStack.length + (documentOverArtifact ? 1 : 0)
  );
  useEffect(() => {
    const isOpening = fileViewerData.isOpen && !fileViewerData.doNotOpenDrawer;
    // An entity preview or an artifact pane keeps the drawer up on its own; a
    // document closing out from under it must not take them with it. One
    // that a hand-off has let go of, still on screen for the slide, does not
    // count.
    const departing = departingRef.current;
    const paneHeld = artifactPane !== null && artifactPane !== departing.pane;
    const stackHeld =
      entityPreviewStack.length > 0 && entityPreviewStack !== departing.stack;
    setOpen(isOpening || stackHeld || paneHeld);
    // `doNotOpenDrawer` is a dependency, not just an input: the viewer-mode
    // hand-off keeps the same document open and only flips that flag, so
    // keying on `isOpen` alone leaves the drawer shut after the split pane has
    // already let go — the document disappears (#9746).
  }, [
    fileViewerData.isOpen,
    fileViewerData.doNotOpenDrawer,
    entityPreviewStack.length,
    artifactPane,
  ]);

  // Handle entity preview events. In overlay mode the drawer is the only pane
  // there is, so it has to be able to open cold for an entity — not just stack
  // one on top of an already-open document the way it originally did (#9746).
  useEffect(() => {
    const handleEntityPreview = (event: CustomEvent) => {
      // A record opened from an artifact on screen here stacks on it, whatever
      // the surface's mode (#12452); "back" returns to the artifact.
      if (!opensInDrawer(window.location.pathname)) return;
      // Inside the RMS there is no entity preview in any mode — Layout turns
      // the event into a navigation instead (#9746).
      if (
        shouldNavigateToEntity(window.location.pathname, {
          insidePreviewPane: false,
        })
      ) {
        return;
      }
      const { entityKind, entityId, factName } = event.detail;
      setEntityPreviewStack((prev) => [
        ...prev,
        { entityKind, entityId, factNames: factName ? [factName] : undefined },
      ]);
      setOpen(true);
    };
    window.addEventListener(
      "openEntityPreview",
      handleEntityPreview as EventListener
    );
    return () =>
      window.removeEventListener(
        "openEntityPreview",
        handleEntityPreview as EventListener
      );
  }, []);

  // Artifact panes in the drawer (Gerry, 2026-09-01): Layout forwards any
  // artifact-kind openDocumentPanel here while the surface is in overlay
  // mode. A file-shaped detail (previewUrl, no editor payload) goes through
  // Redux so the existing FileViewer path renders it.
  useEffect(() => {
    const handleArtifactOverlay = (event: CustomEvent) => {
      const detail = event.detail as ArtifactPaneDetail;
      const isEditorPane = Boolean(
        detail.diagram ||
          detail.textArtifact ||
          detail.tableArtifact ||
          detail.editorDocument
      );
      if (!isEditorPane) {
        setArtifactPane(null);
        dispatch(
          setFileViewerDataOnlyNew({
            isOpen: true,
            fileId: null,
            fileName: detail.fileName ?? null,
            fileType: null,
            previewUrl: detail.previewUrl ?? null,
            downloadUrl: detail.downloadUrl ?? null,
            artifactId: detail.artifactId ?? null,
          })
        );
        return;
      }
      setEntityPreviewStack([]);
      setArtifactPane(detail);
      setOpen(true);
      // Told at once, not only by the effect below: Layout may release the
      // document that sat beside this artifact in the same task, and it
      // belongs here with it (#12452).
      useDrawerArtifactStore.setState({ showing: true });
    };
    window.addEventListener(
      OPEN_ARTIFACT_OVERLAY_EVENT,
      handleArtifactOverlay as EventListener
    );
    return () =>
      window.removeEventListener(
        OPEN_ARTIFACT_OVERLAY_EVENT,
        handleArtifactOverlay as EventListener
      );
  }, [dispatch]);

  // Tell Layout while an artifact is on screen here (#12452): a document
  // opened from it, or from anywhere, must come to the drawer rather than
  // open in the split pane behind the drawer's backdrop.
  const showingArtifact = open && artifactPane !== null;
  useEffect(() => {
    useDrawerArtifactStore.setState({ showing: showingArtifact });
    return () => useDrawerArtifactStore.setState({ showing: false });
  }, [showingArtifact]);
  // And while it is open at all: Layout keeps the sidebar it collapsed for
  // a viewer collapsed when that viewer moves here from the split pane.
  useLayoutEffect(() => {
    useDrawerArtifactStore.setState({ open });
    return () => useDrawerArtifactStore.setState({ open: false });
  }, [open]);
  // Each open starts at the widest, before the first frame paints, whatever
  // opened it (a click, a share link, the side-by-side hand-off), so a drag
  // from the last open is gone.
  const wasOpenRef = useRef(false);
  useLayoutEffect(() => {
    if (open && !wasOpenRef.current) {
      setDrawerWidth(getDefaultWidth());
    }
    wasOpenRef.current = open;
  }, [open]);

  // A closed drawer forgets its record stack once the slide has ended; a
  // reopen flushes what an interrupted exit left queued (see handleClose).
  useEffect(() => {
    if (open) {
      flushExitQueue();
      return;
    }
    const stackAtClose = handoffRef.current.entityPreviewStack;
    exitQueueRef.current.push(() =>
      setEntityPreviewStack((prev) => (prev === stackAtClose ? [] : prev))
    );
  }, [open]);

  // The drawer's artifact pane obeys the same closes the split panes obey
  // (review B2): a chat send's transient sweep, an explicit pane close, and
  // Layout's route-change sweep (which fires closeArtifactOverlay) all take
  // it down — an artifact drawer must not outlive its surface.
  useEffect(() => {
    // A document closing beside the artifact is not the artifact closing
    // (#12452): the file viewer marks its own close.
    const closeArtifactPane = (event: Event) => {
      if (documentCloseDetail(event)) return;
      const current = handoffRef.current;
      const pane = current.artifactPane;
      if (pane === null) return;
      // With nothing else on screen this closes the drawer, and the
      // artifact stays for the slide out (review M2); beside a document or
      // a record it is only the artifact going, now.
      const documentShowing =
        current.fileViewerData.isOpen &&
        !current.fileViewerData.doNotOpenDrawer;
      if (documentShowing || current.entityPreviewStack.length > 0) {
        setArtifactPane(null);
        return;
      }
      setOpen(false);
      exitQueueRef.current.push(() =>
        setArtifactPane((prev) => (prev === pane ? null : prev))
      );
    };
    const events = [
      "closeDocumentPanel",
      "closeTransientPanels",
      "closeGlobalFileViewer",
      CLOSE_ARTIFACT_OVERLAY_EVENT,
    ];
    for (const name of events) {
      window.addEventListener(name, closeArtifactPane);
    }
    return () => {
      for (const name of events) {
        window.removeEventListener(name, closeArtifactPane);
      }
    };
  }, []);

  // Switching this surface to split hands what the drawer is showing over to
  // Layout's pane, so the toggle re-presents the open viewer rather than
  // making the user reopen it (#9746). Mirror of the handoff in Layout.
  const handoffRef = useRef({
    open,
    entityPreviewStack,
    fileViewerData,
    artifactPane,
  });
  handoffRef.current = {
    open,
    entityPreviewStack,
    fileViewerData,
    artifactPane,
  };
  useEffect(() => {
    const handleModeChange = () => {
      const artifactSplit = getArtifactViewerMode() === "split";
      const surfaceSplit = getViewerMode(window.location.pathname) === "split";
      if (!artifactSplit && !surfaceSplit) return;
      const current = handoffRef.current;
      if (!current.open) return;

      // An artifact editor pane hands back verbatim (on the ARTIFACT mode,
      // its own memory): the same detail its open used re-enters Layout's
      // split-pane funnel.
      if (current.artifactPane && artifactSplit) {
        const detail = current.artifactPane;
        const beside = current.fileViewerData;
        const documentBeside =
          beside.isOpen &&
          !beside.doNotOpenDrawer &&
          Boolean(beside.fileId || beside.interactionId);
        // The pane keeps showing the artifact while it slides out.
        departingRef.current.pane = detail;
        exitQueueRef.current.push(() => {
          if (departingRef.current.pane === detail) {
            departingRef.current.pane = null;
          }
          setArtifactPane((prev) => (prev === detail ? null : prev));
        });
        setOpen(false);
        window.dispatchEvent(new CustomEvent("openDocumentPanel", { detail }));
        // A document open beside the artifact goes with it (#12452): re-opened
        // once the artifact has left, so the surface's own mode places it
        // rather than this drawer, which no longer holds what it cites.
        if (!documentBeside && beside.isOpen && !beside.doNotOpenDrawer) {
          // Nothing to re-open it by (no blob, no note): it closes with the
          // drawer rather than holding the drawer open alone.
          dispatch(setFileViewerDataOnlyNew({ isOpen: false }));
        }
        if (documentBeside) {
          useDrawerArtifactStore.setState({ showing: false });
          window.dispatchEvent(
            new CustomEvent("openDocumentPanel", {
              // Still the artifact's source: the page's pane shows it as
              // the artifact's source view, with the way back to it.
              detail: { ...beside, isDirectFileOpen: true, fromArtifact: true },
            })
          );
        }
        return;
      }
      if (current.artifactPane) return;

      const entity = surfaceSplit
        ? current.entityPreviewStack[current.entityPreviewStack.length - 1]
        : null;
      if (entity) {
        const stack = current.entityPreviewStack;
        departingRef.current.stack = stack;
        exitQueueRef.current.push(() => {
          if (departingRef.current.stack === stack) {
            departingRef.current.stack = null;
          }
          setEntityPreviewStack((prev) => (prev === stack ? [] : prev));
        });
        setOpen(false);
        window.dispatchEvent(
          new CustomEvent("openEntityPreview", {
            // The event's shape is singular; the entry holds the array. Mirror
            // of Layout's hand-off in the other direction (#11369).
            detail: {
              entityKind: entity.entityKind,
              entityId: entity.entityId,
              factName: entity.factNames?.[0],
            },
          })
        );
        return;
      }

      const {
        fileId,
        fileName,
        fileType,
        previewUrl,
        downloadUrl,
        artifactId,
        interactionId,
      } = current.fileViewerData;
      if ((fileId || interactionId) && surfaceSplit) {
        setOpen(false);
        window.dispatchEvent(
          new CustomEvent("openDocumentPanel", {
            detail: { ...current.fileViewerData, isDirectFileOpen: true },
          })
        );
        return;
      }
      // An artifact's produced file has no blob id — it is addressed by its
      // preview/download URLs, which the split pane's produced-file branch
      // reads (Gerry, 2026-09-01: file artifacts honor the ARTIFACT mode).
      if (!fileId && previewUrl && artifactSplit) {
        setOpen(false);
        window.dispatchEvent(
          new CustomEvent("openDocumentPanel", {
            detail: { fileName, previewUrl, downloadUrl, artifactId },
          })
        );
      }
    };
    window.addEventListener(VIEWER_MODE_CHANGE_EVENT, handleModeChange);
    return () =>
      window.removeEventListener(VIEWER_MODE_CHANGE_EVENT, handleModeChange);
  }, [dispatch]);

  const entityPreviewData =
    entityPreviewStack.length > 0
      ? entityPreviewStack[entityPreviewStack.length - 1]
      : null;

  const handleResize = useCallback(() => {
    setDrawerWidth((prev) => clampWidth(prev));
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const artifactContent = artifactPane?.diagram ? (
    <DiagramPanel
      diagram={artifactPane.diagram}
      title={artifactPane.fileName ?? "Diagram"}
      artifactId={artifactPane.artifactId ?? undefined}
      editable={artifactPane.artifactEditable}
      onClose={handleClose}
    />
  ) : artifactPane?.textArtifact ? (
    <TextArtifactPanel
      artifactId={artifactPane.textArtifact.artifactId}
      editable={artifactPane.textArtifact.editable}
      title={artifactPane.textArtifact.title}
      filename={artifactPane.textArtifact.filename}
      downloadUrl={artifactPane.textArtifact.downloadUrl}
      onClose={handleClose}
    />
  ) : artifactPane?.tableArtifact ? (
    <TableArtifactPanel
      artifactId={artifactPane.tableArtifact.artifactId}
      editable={artifactPane.tableArtifact.editable}
      title={artifactPane.tableArtifact.title}
      content={artifactPane.tableArtifact.content}
      citedValues={artifactPane.tableArtifact.citedValues}
      cellLink={artifactPane.tableArtifact.cellLink}
      onClose={handleClose}
    />
  ) : artifactPane?.editorDocument ? (
    <ChatEditorPanel
      hosted
      departing={departingRef.current.pane === artifactPane}
      workspaceId={artifactPane.editorDocument.workspaceId}
      title={artifactPane.editorDocument.title}
      conversationId={null}
      artifactId={artifactPane.editorDocument.artifactId}
      onClose={handleClose}
    />
  ) : null;

  // The artifact and the document it cites, read together: two columns, and
  // the drawer at its widest for as long as both show. The remembered width
  // belongs to the single pane and is not touched.
  const sideBySide = documentOverArtifact && !phone && !entityPreviewData;
  // The artifact stays mounted, in one place, under whatever covers it (a
  // record preview, or the document at phone): remounting it would drop its
  // scroll position and any draft, and re-read its citations.
  const artifactVisible = !entityPreviewData && (sideBySide || !documentOpen);
  const showsViewer = artifactContent === null || documentOpen;

  // One body for both presentations — only the desktop branch wraps it in
  // the resize affordance.
  const drawerContent = (
    <div
      // One floating card inset from the window's edge (#12481, Gerry),
      // the same frame the split pane wears; at phone it fills the screen.
      className={
        phone
          ? "relative flex h-full bg-[var(--fp-surface)]"
          : "relative m-3 flex h-[calc(100%-24px)] overflow-hidden rounded-[var(--fp-card-r)] border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] [box-shadow:var(--fp-lift-overlay)]"
      }
      data-document-drawer
      // The overlay is a pane host too (#11819): Escape closes it,
      // never the running turn behind it.
      data-viewer-pane
    >
      {artifactContent && (
        <div
          className={artifactVisible ? "h-full min-w-0 flex-1" : "hidden"}
          data-drawer-artifact
        >
          {artifactContent}
        </div>
      )}
      {entityPreviewData ? (
        <div className="h-full min-w-0 flex-1">
          <RmsEntityPreview
            entityKind={entityPreviewData.entityKind}
            entityId={entityPreviewData.entityId}
            factNames={entityPreviewData.factNames}
            onClose={handleClose}
            onBack={() => setEntityPreviewStack((prev) => prev.slice(0, -1))}
          />
        </div>
      ) : showsViewer ? (
        <div
          className={`h-full min-w-0 flex-1 ${
            sideBySide ? "border-l border-[var(--fp-border)]" : ""
          }`}
          data-drawer-document
        >
          {fileViewerData.fileType === "iodl-source" ? (
            <IodlSourceViewer onClose={handleClose} />
          ) : (
            // Beside an artifact the document has half the drawer; the split
            // variant starts with its Details pane shut.
            <FileViewer
              variant={sideBySide ? "split" : "overlay"}
              besideArtifact={sideBySide}
            />
          )}
        </div>
      ) : null}
    </div>
  );

  // Escape closes the drawer from anywhere on the page (#11835), the way
  // Layout closes the split pane. MUI's own Escape handling only sees the
  // keystroke while focus is inside the drawer, and closing the version
  // history column (whose controls held focus) leaves focus on <body> — so
  // without this the next Escape did nothing. Same precedence rules as the
  // split pane: a history column, an editable or a nested dialog that
  // consumed the key wins.
  useEffect(() => {
    const captureEscapePrecedence = (e: KeyboardEvent) =>
      captureDocumentViewerEscapePrecedence(e, document);
    const handleEscape = (e: KeyboardEvent) => {
      if (!handoffRef.current.open) return;
      if (!shouldHandleDocumentViewerEscape(e, document)) return;
      handleCloseRef.current();
    };
    document.addEventListener("keydown", captureEscapePrecedence, true);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", captureEscapePrecedence, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <div>
      <Drawer
        open={open}
        // Escape closes the version history first (#11819) and is consumed
        // there; the drawer must not also close on the same keystroke
        // (#11835: it did, taking the pane down with the history). A click
        // on the backdrop still closes.
        onClose={(event, reason) => {
          // MUI hands over the React keyboard event; the history column
          // prevented the native event in a document capture listener, so
          // the synthetic event already reads as prevented here.
          if (
            reason === "escapeKeyDown" &&
            (event as React.KeyboardEvent).defaultPrevented
          )
            return;
          handleClose();
        }}
        anchor="right"
        transitionDuration={{
          enter: motionTokenMs("--fp-motion-drawer-enter", 220),
          exit: motionTokenMs("--fp-motion-drawer-exit", 180),
        }}
        SlideProps={{ onExited: flushExitQueue }}
        // The design system's quiet scrim (#12481): MUI's half-black
        // backdrop made the slide-in read heavy and the page behind it
        // vanish; the page stays readable under the overlay.
        slotProps={{
          backdrop: { sx: { backgroundColor: "var(--fp-scrim)" } },
        }}
        PaperProps={{
          sx: phone
            ? {
                width: "100vw",
                maxWidth: "none",
                height: "100dvh",
                overflow: "visible",
              }
            : {
                width: "auto",
                maxWidth: "none",
                overflow: "visible",
                // The card inside carries the surface and the shadow; the
                // paper itself paints nothing (#12481).
                bgcolor: "transparent",
                boxShadow: "none",
              },
        }}
      >
        {phone ? (
          drawerContent
        ) : (
          <Resizable
            size={{
              width: sideBySide ? `${MAX_WIDTH_VW}vw` : drawerWidth,
              height: "100vh",
            }}
            minWidth={`${MIN_WIDTH_VW}vw`}
            maxWidth={`${MAX_WIDTH_VW}vw`}
            enable={{ left: !sideBySide }}
            onResizeStop={(_e, _dir, _ref, d) => {
              setDrawerWidth(clampWidth(drawerWidth + d.width));
            }}
            handleComponent={{ left: <LeftHandle width={drawerWidth} /> }}
            handleStyles={{ left: { width: 14, left: 0 } }}
          >
            {drawerContent}
          </Resizable>
        )}
      </Drawer>
    </div>
  );
};

export default DocumentViewDrawer;
